import { useState } from 'react';
import type { components } from '@trackercop/contracts';
import { Badge } from '@/components/ui/badge';
import { ScriptCategoryChip, PayloadCategoryChip } from '@/components/category-chip';

type DriftReport = components['schemas']['DriftReport'];
type DriftedScript = components['schemas']['DriftedScript'];

interface DiffReportProps {
  report: DriftReport;
  earlierLabel: string;
  laterLabel: string;
}

// Renders a DriftReport across five sections: scripts (added / removed /
// drifted), cookies (out of scope for the server diff today — surfaced
// as a count stub when the server adds the field), destinations,
// payload categories by script, GTM tags. Drifted scripts are the
// trojan-horse signal — known URL, changed behaviour — and get top
// billing.
export function DiffReport({ report, earlierLabel, laterLabel }: DiffReportProps) {
  const added = report.newScripts ?? [];
  const removed = report.removedScripts ?? [];
  const drifted = report.driftedScripts ?? [];
  const newDests = report.newDestinations ?? [];
  const newCats = report.newPayloadCategories ?? [];
  const newGTM = report.newGTMTags ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="diff-report">
      <SummaryBanner
        addedCount={added.length}
        driftedCount={drifted.length}
        newDestCount={newDests.length}
        newCategoryCount={newCats.length}
        earlierLabel={earlierLabel}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title={earlierLabel} testid="diff-panel-earlier">
          <SectionCountRow label="Removed scripts" count={removed.length} tone="remove" />
          <div className="flex flex-col gap-1">
            {removed.slice(0, 25).map((s) => (
              <ScriptRow key={s.id} script={s} tone="remove" />
            ))}
          </div>
        </Panel>
        <Panel title={laterLabel} testid="diff-panel-later">
          <SectionCountRow label="Added scripts" count={added.length} tone="add" />
          <div className="flex flex-col gap-1">
            {added.slice(0, 25).map((s) => (
              <ScriptRow key={s.id} script={s} tone="add" />
            ))}
          </div>
        </Panel>
      </div>

      <DriftedScriptsSection drifted={drifted} />

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="New destinations" testid="diff-panel-destinations">
          <SectionCountRow label="Third-party domains" count={newDests.length} tone="warn" />
          <ul className="flex flex-wrap gap-1 text-xs">
            {newDests.map((d) => (
              <li key={d} className="rounded-full border px-2 py-0.5 font-mono">{d}</li>
            ))}
          </ul>
        </Panel>
        <Panel title="New payload categories" testid="diff-panel-categories">
          <SectionCountRow label="Categories added" count={newCats.length} tone="warn" />
          <div className="flex flex-wrap gap-1">
            {newCats.map((c) => (
              <PayloadCategoryChip key={c} category={c} />
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="New GTM tags" testid="diff-panel-gtm">
        <SectionCountRow label="GTM tags" count={newGTM.length} tone="warn" />
        <ul className="flex flex-col gap-1 text-xs">
          {newGTM.slice(0, 25).map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 rounded-sm border bg-muted/30 p-1.5">
              <span className="font-mono">{t.tagName ?? '(unnamed)'}</span>
              <span className="text-muted-foreground">{t.tagType ?? '—'}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function SummaryBanner({
  addedCount,
  driftedCount,
  newDestCount,
  newCategoryCount,
  earlierLabel,
}: {
  addedCount: number;
  driftedCount: number;
  newDestCount: number;
  newCategoryCount: number;
  earlierLabel: string;
}) {
  return (
    <div
      className="rounded-md border bg-muted/30 px-4 py-3 text-sm"
      data-testid="diff-summary-banner"
    >
      <span className="font-semibold">{addedCount}</span> new script{addedCount === 1 ? '' : 's'},{' '}
      <span className="font-semibold">{driftedCount}</span> drifted,{' '}
      <span className="font-semibold">{newDestCount}</span> new destination{newDestCount === 1 ? '' : 's'},{' '}
      <span className="font-semibold">{newCategoryCount}</span> new categor{newCategoryCount === 1 ? 'y' : 'ies'}
      {' '}since {earlierLabel}.
    </div>
  );
}

function Panel({ title, children, testid }: { title: string; children: React.ReactNode; testid?: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function SectionCountRow({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'add' | 'remove' | 'warn';
}) {
  const variant = tone === 'add' ? 'success' : tone === 'remove' ? 'destructive' : 'warning';
  return (
    <div className="flex items-center justify-between text-xs">
      <span>{label}</span>
      <Badge variant={variant} data-testid={`diff-count-${tone}`}>{count}</Badge>
    </div>
  );
}

function ScriptRow({
  script,
  tone,
}: {
  script: components['schemas']['DetectedScript'];
  tone: 'add' | 'remove';
}) {
  const bg = tone === 'add' ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200';
  return (
    <div className={`rounded-sm border p-1.5 text-xs ${bg}`}>
      <div className="flex items-center gap-1">
        <ScriptCategoryChip category={script.category} />
        <span className="font-mono text-[10px]">{script.domain}</span>
      </div>
      <div className="truncate font-mono text-[10px] text-muted-foreground">{script.url}</div>
    </div>
  );
}

function DriftedScriptsSection({ drifted }: { drifted: DriftedScript[] }) {
  return (
    <Panel title="Drifted scripts" testid="diff-panel-drifted">
      <SectionCountRow label="Drifted" count={drifted.length} tone="warn" />
      <p className="text-[11px] text-muted-foreground">
        Same URL, changed behaviour. This is the trojan-horse signal — a reviewer should open each row to see
        which destinations, cookies, or categories the script started or stopped emitting.
      </p>
      <div className="flex flex-col gap-1">
        {drifted.map((d) => (
          <DriftedRow key={d.scriptUrl} drifted={d} />
        ))}
      </div>
    </Panel>
  );
}

function DriftedRow({ drifted }: { drifted: DriftedScript }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-sm border border-amber-300 bg-amber-50 p-1.5 text-xs" data-testid="diff-drifted-row">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="truncate font-mono text-[10px]">{drifted.scriptUrl}</span>
        <span className="text-[10px] text-muted-foreground">{open ? 'Hide' : 'Show'} changes</span>
      </button>
      {open && (
        <div className="mt-1 flex flex-col gap-0.5 rounded-sm bg-white/70 p-1.5">
          <div className="text-[10px] text-muted-foreground">
            {drifted.oldFingerprint.slice(0, 12)}… → {drifted.newFingerprint.slice(0, 12)}…
          </div>
          <ul className="list-disc pl-4 text-[11px]">
            {drifted.changes.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
